import { Draggable } from "@hello-pangea/dnd";
import { ActionIcon, rem, Text, Tooltip } from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconGripVertical,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Fragment, memo, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { timeRegex } from "src/generator/utils/datetime.utils";
import { useDispatch, useSelector } from "src/redux/hooks";
import { reorderRows } from "src/redux/slices/generatorSlice";
import {
  Flex,
  StyledSpaceCell,
  StyledSplitTh,
  StyledTable,
  StyledTd,
  StyledTd2,
  StyledTh,
} from "src/styled";

import { useTheme } from "../../../hooks";
import { type ICol } from "../../../interfaces";
import AddCommentsButton from "../../_Buttons/AddCommentsButton/AddCommentsButton";
import DeleteRowButton from "../../_Buttons/DeleteRowButton/DeleteRowButton";
import NMEARefreshButton from "../../_Buttons/NMEARefreshButton/NMEARefreshButton";
import Cell from "../../Cell/Cell";
import { OVERLAY_PREF } from "../../constants";
import { useCanEditEntry } from "../../hooks/useCanEditEntry";
import { useShowContent } from "../../hooks/useShowContent";
import { getCellWidth } from "../../utils/getCellWidth";
import { generateHeaders } from "../PortraitSection/PortraitSectionHeaders/generateHeaders";
import useCellProps from "./useCellProps";
import DateTimeDisp from "src/components/shared/DateTime/DateTimeDisp";
import {
  getColSpan,
  getHiddenCellBorderFlags,
  getVisibleCellBorderFlags,
} from "./hiddenCellBorders";

interface IRow {
  rows: any[];
  isDynamic: boolean;
  sectionIndex: number;
  draggable?: boolean;
  authRequiredForDeletion?: boolean;
  showAddCommentButton?: boolean;
  isOverlay?: boolean;
  headers?: ICol[];
  showSubRowHeaders?: boolean;
  strikethroughRows?: boolean;
  buttonOrientation?: "vertical" | "horizontal";
  useMarginWithRows?: boolean;
  NMEAButton?: boolean;
  isShared?: boolean;
  sharedIndex?: number;
  date?: string;
  dateUTC?: string;
}

// Utility function to extract date and time from a row
const getRowDateTimeTooltip = (row: any): string | null => {
  const colsToCheck = row.subrows ? row.subrows[0]?.cols : row.cols;
  if (!colsToCheck) return null;

  let dateStr = "";
  let timeStr = "";

  // Look for date and time columns
  for (const col of Object.values(colsToCheck)) {
    const colData = col as any;

    // Extract date
    if (colData.unit === "date" && colData.value) {
      if (typeof colData.value === "string") {
        try {
          dateStr = dayjs(colData.value).format("DD MMM YYYY");
        } catch {
          continue;
        }
      } else if (typeof colData.value === "object" && colData.value.value) {
        try {
          dateStr = dayjs(colData.value.value).format("DD MMM YYYY");
        } catch {
          continue;
        }
      }
    }

    // Extract time
    if (
      colData.value &&
      typeof colData.value === "object" &&
      colData.value.time &&
      (colData.unit === "24lock" ||
        colData.unit === "time" ||
        colData.name?.toLowerCase().includes("time"))
    ) {
      const timeString = colData.value.time;
      if (typeof timeString === "string" && timeRegex.test(timeString)) {
        timeStr = timeString;
      }
    }

    // Also check for direct time string values
    if (
      colData.unit === "time" &&
      colData.value &&
      typeof colData.value === "string" &&
      timeRegex.test(colData.value)
    ) {
      timeStr = colData.value;
    }
  }

  // Format tooltip text
  if (dateStr && timeStr) {
    return `${dateStr} ${timeStr}`;
  } else if (dateStr) {
    return dateStr;
  } else if (timeStr) {
    return timeStr;
  }

  return null;
};

const Row = ({
  rows,
  isDynamic,
  sectionIndex,
  draggable,
  authRequiredForDeletion,
  showAddCommentButton,
  isOverlay,
  headers,
  showSubRowHeaders,
  strikethroughRows,
  buttonOrientation,
  useMarginWithRows,
  NMEAButton,
  isShared,
  sharedIndex,
}: IRow) => {
  const dispatch = useDispatch();
  const shipType = useSelector(state => state.generatorSlice.shipType);
  const canEditEntry = useCanEditEntry();
  const { idType } = useParams();
  const { hiddenStyles, propGetter } = useCellProps(Boolean(isOverlay));
  const theme = useTheme();
  const { shouldHideElement } = useShowContent();
  const features = useSelector(state => state.generatorSlice.features);
  const isBridgeLog = useSelector(
    state => state.generatorSlice.lbentry.isBridgeLog,
  );
  const savedRows = useSelector(
    state => state.generatorSlice.lbentry.sections[sectionIndex]?.savedRows,
  );
  let showDateTimePerRow = useSelector(
    state =>
      state.generatorSlice.lbentry.sections[sectionIndex]?.showDateTimePerRow,
  );
  const { showDateTimePerRow: globalShowDateTimePerRow } = useSelector(
    state => state.generatorSlice.lbentry,
  );
  showDateTimePerRow = showDateTimePerRow ?? globalShowDateTimePerRow;

  const shouldRowBeGrayed = useCallback(
    (row: any, rowIndex: number) => {
      if (savedRows?.[rowIndex] !== undefined) {
        return savedRows[rowIndex];
      }

      return (
        row.cols &&
        Object.values(row.cols).every((col: any) => col.saved === true)
      );
    },
    [savedRows],
  );

  const getRowDates = useCallback((row: IRow) => {
    dayjs.extend(customParseFormat);
    const date = row.date ? dayjs(row.date).format() : null;
    const dateUTC = row.dateUTC ? dayjs(row.dateUTC).utc().format() : null;
    console.log({ date, dateUTC });
    return { date, dateUTC };
  }, []);

  const getTooltipContent = useCallback(
    (row: IRow): string | JSX.Element | null => {
      if (showDateTimePerRow) {
        const { date, dateUTC } = getRowDates(row);
        return <DateTimeDisp color="inherit" lt={date} utc={dateUTC} />;
      } else if (isDynamic && isBridgeLog) {
        const tooltipFromCols = getRowDateTimeTooltip(row);
        if (tooltipFromCols) return tooltipFromCols;
      }

      return null;
    },
    [isDynamic, isBridgeLog, showDateTimePerRow, getRowDates],
  );

  const renderRows = useCallback(() => {
    // eslint-disable-next-line array-callback-return
    if (!rows || rows.length === 0) return null;

    // let totalRows = 0;
    // Object.values(rows).map(
    //   item => item && Object.keys(item.cols).length > 0 && totalRows++,
    // );

    const generateRows = (
      rowsToGenerate: any[],
      inheritedDraggableId?: string,
      inheritedRowIndex?: number,
      totalRows?: number,
    ): any =>
      rowsToGenerate.map((row, idx) => {
        const rowIndex = inheritedRowIndex ?? idx;
        const dataIndex = inheritedRowIndex ?? row.originalIndex ?? idx;

        if (row.subrows) {
          const totalRows = row.subrows.length;
          const generatedSubrows = generateRows(
            row.subrows,
            row.symbol,
            idx,
            totalRows,
          );

          if (useMarginWithRows && idx < rowsToGenerate.length - 1) {
            generatedSubrows.push(
              <tr
                key={`margin-${row.symbol || dataIndex}-${idx}`}
                className="row-margin-separator"
                style={{
                  height: "30px",
                  border: "none",
                  background: "transparent",
                }}>
                <td colSpan={999} style={{ border: "none", padding: 0 }} />
              </tr>,
            );
          }

          return generatedSubrows;
        }
        const isEditableAndDynamic =
          canEditEntry &&
          isDynamic &&
          (inheritedRowIndex === undefined ? true : idx === 0);
        const canSeeActions =
          isEditableAndDynamic &&
          !(inheritedDraggableId && (showSubRowHeaders ? idx === 0 : idx > 0));

        const hasStrikethrough = Object.values(row.cols).some(
          (c: any) => c.strikethrough,
        );

        if (Object.getOwnPropertyNames(row.cols || {}).length > 0) {
          let columnOffset = 0;
          const generatedCells = Object.keys(row.cols).map((uuid, index) => {
            const cell = row.cols[uuid];
            const colSpan = getColSpan(cell);

            if (buttonOrientation === "vertical" && cell.unit === "BLANK") {
              columnOffset += colSpan;
              return null;
            }

            // Hidden cells (still need width to prevent layout issues).
            if (cell.hidden) {
              const {
                showTopBorder,
                showBottomBorder,
                showLeftBorder,
                showRightBorder,
              } = getHiddenCellBorderFlags(
                rowsToGenerate,
                rowIndex,
                columnOffset,
                colSpan,
                shouldHideElement,
                buttonOrientation,
              );
              columnOffset += colSpan;
              return (
                <StyledTd2
                  key={uuid}
                  colSpan={colSpan}
                  showTopBorder={showTopBorder}
                  showBottomBorder={showBottomBorder}
                  showLeftBorder={showLeftBorder}
                  showRightBorder={showRightBorder}
                  isBlankSpacer={cell.unit === "BLANK"}
                  {...propGetter({ minWidth: getCellWidth(cell.width) })}
                  grayed={
                    (shipType === "1" && cell.class === "grp-pass") ||
                    cell.grayed
                  }></StyledTd2>
              );
            }

            // Header styled cells
            if (cell.headerStyle) {
              const title = [];
              if (cell.name && cell.name.indexOf("<br>") > -1) {
                const arr = cell.name.split("<br>");
                arr.forEach((v: string, titleIdx: number) =>
                  title.push(
                    <StyledSplitTh key={`${uuid}-${titleIdx}`}>
                      {v}
                    </StyledSplitTh>,
                  ),
                );
              } else {
                title.push(cell.name);
              }

              let className = "";
              if (cell.tag === "ss-engine-header") {
                className += " ss-engine-header";
              }

              const { showLeftBorder } = getVisibleCellBorderFlags(
                row,
                columnOffset,
              );
              columnOffset += colSpan;
              return (
                <StyledTh
                  key={uuid}
                  colSpan={colSpan}
                  showLeftBorder={showLeftBorder}
                  style={{ minWidth: getCellWidth(cell.width) }}>
                  <Text
                    size="sm"
                    c="inherit"
                    span
                    style={{ position: "relative" }}
                    className={className}>
                    {title}
                  </Text>
                </StyledTh>
              );
            }

            if (cell.unit === "space" || cell.type === "space") {
              columnOffset += colSpan;
              return (
                <StyledSpaceCell
                  key={uuid}
                  style={{ minWidth: getCellWidth(cell.width) }}
                />
              );
            }

            // hidden cell ignored by layout
            const shouldHide = shouldHideElement(cell);
            if (shouldHide) {
              columnOffset += colSpan;
              return null;
            }

            const { showLeftBorder } = getVisibleCellBorderFlags(
              row,
              columnOffset,
            );
            columnOffset += colSpan;
            return (
              <StyledTd
                key={uuid}
                colSpan={colSpan}
                rowSpan={cell.rowspan ?? 1}
                noBorder={cell.noBorder}
                showLeftBorder={showLeftBorder}
                {...propGetter({
                  minWidth: getCellWidth(cell.width),
                  width: cell.explicitWidth ? getCellWidth(cell.width) : "",
                })}
                grayed={shipType === "1" && cell.class === "grp-pass"}>
                <Cell
                  unitType={cell.unit}
                  uuid={isOverlay ? OVERLAY_PREF + uuid : uuid}
                  rowIndex={dataIndex}
                  subrowIndex={inheritedRowIndex ? idx : undefined}
                  colIndex={index}
                  sectionIndex={sectionIndex}
                  cellId={cell.id}
                  cols={cell?.content?.cols}
                  flex={cell?.content?.flex}
                  isOverlay={isOverlay}
                  disabled={
                    cell.disabled ||
                    cell.strikethrough ||
                    (cell.location === "anchor" && cell.underway === true)
                  }
                  grayed={
                    cell.strikethrough ||
                    (cell.location === "anchor" && cell.underway === true)
                  }
                  isShared={isShared}
                  sharedIndex={sharedIndex}
                  // totalRows={totalRows}
                  {...cell}
                />
              </StyledTd>
            );
          });
          const shouldGenerateSubRowHeaders =
            ((rowIndex === 0 && idx > 0) || rowIndex !== 0) &&
            showSubRowHeaders &&
            headers &&
            Array.isArray(headers[idx]);
          const generatedSubRowHeaders = shouldGenerateSubRowHeaders
            ? generateHeaders({
                headers: headers[idx] as unknown as ICol[],
                theme,
                omitThead: true,
                draggable: draggable && idx === 0,
                isDynamic: isDynamic && idx === 1,
                sectionIndex,
              })
            : null;

          // Get tooltip content for dynamic rows only
          const tooltipContent = getTooltipContent(row);

          const content = (
            <Fragment>
              {generatedSubRowHeaders}

              <Draggable
                // key={uuidv4()} // commented out because rows are jumping
                index={rowIndex}
                draggableId={inheritedDraggableId ?? row.symbol}>
                {(provided, snapshot) => {
                  const rowElement = (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        marginTop:
                          inheritedDraggableId && idx > 0 && snapshot.isDragging
                            ? "50px"
                            : "auto",
                        ...hiddenStyles,
                        textDecoration: hasStrikethrough
                          ? "line-through"
                          : "none",
                        opacity: shouldRowBeGrayed(row, dataIndex) ? 0.4 : 1,
                      }}>
                      {!(inheritedDraggableId && idx > 0) && (
                        <StyledTd
                          rowSpan={
                            buttonOrientation === "vertical" && totalRows
                              ? totalRows
                              : 1
                          }
                          valign={
                            buttonOrientation === "vertical"
                              ? "middle"
                              : undefined
                          }
                          {...propGetter({ width: "60px" })}>
                          <Flex
                            {...(buttonOrientation !== "vertical"
                              ? provided.dragHandleProps
                              : {})}
                            style={{ height: "100%" }}
                            justify="center"
                            align="center"
                            direction={
                              buttonOrientation === "vertical"
                                ? "column"
                                : "row"
                            }>
                            {buttonOrientation === "vertical" ? (
                              <Fragment>
                                <ActionIcon
                                  size="md"
                                  disabled={rowIndex === 0}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const fromIndex = dataIndex;
                                    const toIndex =
                                      rows[rowIndex - 1]?.originalIndex ??
                                      rowIndex - 1;
                                    dispatch(
                                      reorderRows({
                                        sectionIndex,
                                        from: fromIndex,
                                        to: toIndex,
                                      }),
                                    );
                                  }}>
                                  <IconChevronUp
                                    style={{ width: rem(20), height: rem(20) }}
                                    stroke={2}
                                  />
                                </ActionIcon>
                                <ActionIcon
                                  size="md"
                                  disabled={rowIndex === rows.length - 1}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const fromIndex = dataIndex;
                                    const toIndex =
                                      rows[rowIndex + 1]?.originalIndex ??
                                      rowIndex + 1;
                                    dispatch(
                                      reorderRows({
                                        sectionIndex,
                                        from: fromIndex,
                                        to: toIndex,
                                      }),
                                    );
                                  }}>
                                  <IconChevronDown
                                    style={{ width: rem(20), height: rem(20) }}
                                    stroke={2}
                                  />
                                </ActionIcon>
                              </Fragment>
                            ) : (
                              <IconGripVertical
                                style={{ width: rem(18), height: rem(18) }}
                                stroke={1.5}
                              />
                            )}
                          </Flex>
                        </StyledTd>
                      )}
                      {generatedCells}
                      {NMEAButton &&
                        !(
                          buttonOrientation === "vertical" &&
                          inheritedDraggableId &&
                          idx > 0
                        ) && (
                          <NMEARefreshButton
                            rowIndex={dataIndex}
                            sectionIndex={sectionIndex}
                            hidden={isOverlay}
                            width="60px"
                            rowSpan={
                              buttonOrientation === "vertical" && totalRows
                                ? totalRows
                                : 1
                            }
                          />
                        )}
                      {canSeeActions && (
                        <Fragment>
                          <DeleteRowButton
                            hidden={isOverlay}
                            isAuthorizationRequired={authRequiredForDeletion}
                            rowIndex={dataIndex}
                            rowSpan={
                              buttonOrientation === "vertical" && totalRows
                                ? totalRows
                                : 1
                            }
                            sectionIndex={sectionIndex}
                            rowNumber={rows.length}
                            strikethroughRows={strikethroughRows}
                            hasStrikethrough={hasStrikethrough}
                            width="60px"
                          />
                          {showAddCommentButton && (
                            <StyledTd style={hiddenStyles}>
                              <AddCommentsButton
                                sectionIndex={sectionIndex}
                                rowIndex={dataIndex}
                              />
                            </StyledTd>
                          )}
                        </Fragment>
                      )}
                    </tr>
                  );

                  // Wrap with tooltip only if it's a dynamic row with date/time content
                  return tooltipContent ? (
                    <Tooltip label={tooltipContent} withArrow position="top">
                      {rowElement}
                    </Tooltip>
                  ) : (
                    rowElement
                  );
                }}
              </Draggable>
            </Fragment>
          );
          if (draggable) {
            if (shouldGenerateSubRowHeaders)
              return (
                <tr key={`header-${idx}`} style={{ border: "none" }}>
                  <td style={{ padding: 0, border: "none" }} colSpan={999}>
                    <StyledTable
                      style={{ border: "none" }}
                      className="no-border">
                      {content}
                    </StyledTable>
                  </td>
                </tr>
              );
            return content;
          }

          const rowElement = (
            <tr
              key={row.symbol || `${sectionIndex}-${dataIndex}-${idx}`}
              {...propGetter({
                backgroundColor: theme?.colors.tdBackgroundColor[0],
              })}>
              {generatedCells}
              {isEditableAndDynamic && (
                <DeleteRowButton
                  hidden={isOverlay}
                  isAuthorizationRequired={authRequiredForDeletion}
                  rowIndex={rowIndex}
                  sectionIndex={sectionIndex}
                  rowNumber={
                    inheritedRowIndex === undefined
                      ? rowsToGenerate.length
                      : (totalRows ?? 0)
                  }
                  strikethroughRows={strikethroughRows}
                  hasStrikethrough={hasStrikethrough}
                />
              )}
              {/* {isEditableAndDynamic && ( */}
              {/*   <StyledTd> */}
              {/*     <AddCommentsButton */}
              {/*       sectionIndex={sectionIndex} */}
              {/*       rowIndex={idx} */}
              {/*     /> */}
              {/*   </StyledTd> */}
              {/* )} */}
            </tr>
          );

          // Wrap with tooltip only if it's a dynamic row with date/time content
          return tooltipContent ? (
            <Tooltip label={tooltipContent} withArrow position="top">
              {rowElement}
            </Tooltip>
          ) : (
            rowElement
          );
        }
        return null;
      });
    return generateRows(rows).flat();
  }, [
    authRequiredForDeletion,
    canEditEntry,
    draggable,
    isDynamic,
    rows,
    sectionIndex,
    shipType,
    features,
    shouldHideElement,
    headers,
  ]);
  const renderedRows = useMemo(() => renderRows(), [renderRows]);
  /// IMPORTANT!!!!! this is a fix for logbook having trouble with inputs that have lists in them!!!!!
  if (idType === "meid") {
    return <tbody>{renderedRows}</tbody>;
  }

  if (draggable) return <Fragment>{renderedRows}</Fragment>;
  return <tbody>{renderedRows}</tbody>;
};

export default memo(Row);
